import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivate, Router, RouterStateSnapshot, UrlTree } from '@angular/router';
import { MsalService } from '@azure/msal-angular';
import { AccountInfo } from '@azure/msal-browser';
import { DeviceDetectorService } from 'ngx-device-detector';
import { Observable, from, of } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';

import { LocalStorageService } from './connectors/localstorage';
import { MenuBootstrapService } from './home/core/services/menu-bootstrap.service';
import { AuthService } from './services/auth/authService.service';
import { CurrentUserContextService } from './services/auth/current-user-context.service';
import { LogoutService } from './services/auth/logout.service';
import { MsalBootstrapService } from './services/auth/msal-bootstrap.service';
import { UserBehaviorService } from './services/user-events/user-behavior.service';

@Injectable({
  providedIn: 'root'
})
export class AuthenticationGuard implements CanActivate {
  deviceInfo: unknown;

  constructor(
    private authService: MsalService,
    private userBehavior: UserBehaviorService,
    private router: Router,
    private deviceService: DeviceDetectorService,
    private localStorage: LocalStorageService,
    private logoutService: LogoutService,
    private appAuthService: AuthService,
    private menuBootstrapService: MenuBootstrapService,
    private msalBootstrapService: MsalBootstrapService,
    private currentUserContext: CurrentUserContextService
  ) {
    this.epicFunction();
  }

  canActivate(
    _route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): Observable<boolean | UrlTree> {
    return from(this.msalBootstrapService.completePortalSessionIfNeeded(
      this.authService,
      this.appAuthService
    )).pipe(
      catchError(() => of({ status: 'failed' } as const)),
      switchMap((sessionResult) => {
        if (sessionResult.status !== 'authenticated') {
          return of(this.#redirectToLogin(state.url));
        }

        const loggedByMsal = this.authService.instance.getActiveAccount();
        if (!loggedByMsal) {
          return of(this.#redirectToLogin(state.url));
        }

        this.currentUserContext.initialize();
        this.setMsalLoggedInUser(loggedByMsal);

        const isLandingPage = state.url.startsWith('/home') || state.url === '/' || state.url.startsWith('/login');
        const returnUrl = this.logoutService.getReturnUrl();

        if (returnUrl && isLandingPage && returnUrl !== state.url && returnUrl !== '/login') {
          this.logoutService.consumeReturnUrl();
          return of(this.router.parseUrl(returnUrl));
        }

        return of(true);
      })
    );
  }

  #redirectToLogin(returnUrl: string): UrlTree {
    this.logoutService.setReturnUrl(returnUrl);
    this.reset();
    return this.router.parseUrl('/login');
  }

  reset(): void {
    this.userBehavior.setUser({
      name: '',
      email: '',
      from: ''
    });

    this.userBehavior.resetLocalStorageEvent();

    this.localStorage.remove('profile');
    this.localStorage.remove('userId');

    this.menuBootstrapService.reset();
  }

  setMsalLoggedInUser(user: AccountInfo): void {
    if (!user) {
      return;
    }

    this.userBehavior.setUser({
      name: user.name,
      email: user.username,
      from: 'msal'
    });
  }

  epicFunction(): void {
    this.deviceInfo = this.deviceService.getDeviceInfo();
  }
}
